import _ from 'underscore';
import { useRef, useState, createContext, useContext } from 'react';

import styled, { css } from 'styled-components';
import useOutsideClick from '../custom-hooks/use-outside-click';
import { SettingCheckbox } from '../common/settings';
import AutocompleteInput from '../common/autocomplete-input';
import FullWidthTruncatedDiv from './full-width-truncated-div';

// NOTE: adding a dropdown context to be able to close out of the dropdown from child components without needing to worry
// about passing in onClose handler through each child component. This was done for re-usability and simplicity.
/** @deprecated prefer `import { Menu } from '@workramp/ui';` */
const DropdownContext = createContext({});

/** @deprecated prefer `import { Menu } from '@workramp/ui';` */
const DefaultButton = ({ displayText, src, hideCaret, className, width, displayAsSelect = false }) => {
  const { show } = useContext(DropdownContext);

  return (
    <DefaultButtonWrapper disableClick={hideCaret} className={className} width={width}>
      <DefaultButtonContentWrapper hideCaret={hideCaret} displayAsSelect={displayAsSelect}>
        <DefaultButtonDisplayText>{displayText}</DefaultButtonDisplayText>
        {!hideCaret && <DefaultButtonCaret alt="" src={src || '/images/v2/dropdown-icon.svg'} show={show} />}
      </DefaultButtonContentWrapper>
    </DefaultButtonWrapper>
  );
};

/** @deprecated prefer `import { Menu } from '@workramp/ui';` */
const InputLabelButton = (props) => (
  <InputLabelButtonWrapper className={props.className}>
    <DefaultButton {...props} />
  </InputLabelButtonWrapper>
);

/** @deprecated prefer `import { Menu } from '@workramp/ui';` */
const DefaultDropdownHeader = ({ title, className }) => {
  const { handleClose } = useContext(DropdownContext);

  return (
    <DropdownMenuHeader className={className}>
      <DropdownMenuHeaderTitle>{title}</DropdownMenuHeaderTitle>
      <DropdownMenuHeaderCloseWrapper>
        <DropdownMenuHeaderCloseIcon
          src="/images/cross_icon_v2.svg"
          alt="Close Dropdown"
          onClick={() => handleClose()}
        />
      </DropdownMenuHeaderCloseWrapper>
    </DropdownMenuHeader>
  );
};

/** @deprecated prefer `import { Menu } from '@workramp/ui';` */
const DefaultDropdownMenu = ({
  children,
  direction,
  width,
  withCaret,
  className,
  height = null,
  maxHeight,
  offsets,
}) => (
  <DropdownMenuWrapper
    direction={direction}
    withCaret={withCaret}
    width={width}
    className={className}
    height={height}
    offsets={offsets}
  >
    <DropdownMenuModal fullHeight={height} maxHeight={maxHeight}>
      {children}
    </DropdownMenuModal>
  </DropdownMenuWrapper>
);

/** @deprecated prefer `import { AutocompleteField } from '@workramp/ui';` */
const AutocompleteInputComponent = ({
  autoCompleteProps,
  autoFocus,
  direction = 'S',
  placeholder,
  overwriteInputCssClass,
  className,
  suggestions,
  onChange,
}) => {
  const {
    useUsersAndGroupsDropdown,
    searchURL,
    onEntryClick,
    onClearClick,
    optionRenderer,
    limit,
    maxHeight,
    initialValue = '',
  } = autoCompleteProps;
  const [val, setVal] = useState(initialValue);

  return (
    <AutocompleteInput
      overwriteInputCssClass={overwriteInputCssClass}
      className={className}
      autoFocus={autoFocus}
      value={val}
      customResultsDropdown={(results, closeSuggestionsAndClearInput, activeSuggestion) => (
        <DefaultDropdownMenu direction={direction} maxHeight={maxHeight}>
          <DefaultDropdownSingleSelect
            className="dropdown-single-select"
            optionRenderer={optionRenderer}
            closeOnClick={false}
            options={_.map(results, (result, index) => ({
              label: result.label,
              name: result.name || result.displayName,
              avatar: result.avatar,
              id: result.id,
              isGroup: result.isGroup,
              isUser: result.isUser,
              displayText: result.name || result.displayName,
              isActive: index === activeSuggestion,
              onClick: (option) => {
                onEntryClick(option);
                setVal('');
                closeSuggestionsAndClearInput();
              },
            }))}
          />
        </DefaultDropdownMenu>
      )}
      useUsersAndGroupsDropdown={useUsersAndGroupsDropdown}
      searchURL={searchURL}
      onChange={({ target }) => {
        setVal(target.value);
        onChange?.(target.value);
      }}
      onEntryClick={(entry) => {
        setVal('');
        onEntryClick(entry);
      }}
      onClearClick={onClearClick}
      placeholder={placeholder}
      suggestions={suggestions}
      limit={limit}
    />
  );
};

/** @deprecated prefer `import { AutocompleteField } from '@workramp/ui';` */
const AutocompleteAcademyInputComponent = ({
  autoCompleteProps,
  autoFocus,
  direction = 'S',
  placeholder,
  overwriteInputCssClass,
  className,
  suggestions,
  onChange,
}) => {
  const {
    useUsersAndGroupsDropdown,
    searchURL,
    onEntryClick,
    onClearClick,
    optionRenderer,
    limit,
    maxHeight,
    initialValue = '',
  } = autoCompleteProps;
  const [val, setVal] = useState(initialValue);

  return (
    <AutocompleteInput
      overwriteInputCssClass={overwriteInputCssClass}
      className={className}
      autoFocus={autoFocus}
      value={val}
      customResultsDropdown={(results, closeSuggestionsAndClearInput, activeSuggestion) => (
        <DefaultDropdownMenu direction={direction} maxHeight={maxHeight}>
          <DefaultDropdownSingleSelect
            className="dropdown-single-select"
            optionRenderer={optionRenderer}
            closeOnClick={false}
            options={_.map(results, (result, index) => ({
              label: result.label,
              name: result.name,
              avatar: result.avatar,
              id: result.id,
              isGroup: result.type === 'segment',
              isUser: result.type === 'user',
              displayText: result.name,
              isActive: index === activeSuggestion,
              onClick: (option) => {
                onEntryClick(option);
                setVal('');
                closeSuggestionsAndClearInput();
              },
            }))}
          />
        </DefaultDropdownMenu>
      )}
      useUsersAndGroupsDropdown={useUsersAndGroupsDropdown}
      searchURL={searchURL}
      onChange={({ target }) => {
        setVal(target.value);
        onChange?.(target.value);
      }}
      onEntryClick={(entry) => {
        setVal('');
        onEntryClick(entry);
      }}
      onClearClick={onClearClick}
      placeholder={placeholder}
      suggestions={suggestions}
      limit={limit}
    />
  );
};

/** @deprecated prefer `import { AutocompleteField } from '@workramp/ui';` */
const DefaultInput = ({
  autoCompleteProps,
  autoFocus,
  className,
  direction = 'S',
  hasLeftLabel,
  onChange,
  placeholder,
  value,
  suggestions,
  isAcademy = false,
}) => (
  <>
    {!_.isEmpty(autoCompleteProps) ? (
      <AutocompleteInputWrapper hasLeftLabel={hasLeftLabel} className={className}>
        {!isAcademy && (
          <AutocompleteInputComponent
            autoCompleteProps={autoCompleteProps}
            autoFocus={autoFocus}
            direction={direction}
            overwriteInputCssClass="input-editor-2020"
            placeholder={placeholder}
            suggestions={suggestions}
            onChange={onChange}
            limit={autoCompleteProps.limit}
          />
        )}
        {isAcademy && (
          <AutocompleteAcademyInputComponent
            autoCompleteProps={autoCompleteProps}
            autoFocus={autoFocus}
            direction={direction}
            overwriteInputCssClass="input-editor-2020"
            placeholder={placeholder}
            suggestions={suggestions}
            onChange={onChange}
            limit={autoCompleteProps.limit}
          />
        )}
      </AutocompleteInputWrapper>
    ) : (
      <DropdownInput
        className={className}
        hasLeftLabel={hasLeftLabel}
        autoFocus={autoFocus}
        placeholder={placeholder}
        value={value}
        onChange={({ target }) => onChange(target.value)}
      />
    )}
  </>
);

/** @deprecated prefer `import { AutocompleteField } from '@workramp/ui';` */
const DefaultInputWithLabel = ({ dropdownMenuProps, className, closeOnClick }) => {
  const { labelProps, inputProps, direction } = dropdownMenuProps;
  const { options, currentValue } = labelProps;
  const labelDisplayText = _.find(options, (option) => option.value === currentValue).displayText;

  // TODO: onInputButtonClick, autofocus to input field
  return (
    <InputWithLabel className={className}>
      <DropdownV2
        button={<InputLabelButton displayText={labelDisplayText} hideCaret={_.size(options) === 1} />}
        allowClick={false}
      >
        <DefaultDropdownMenu direction={direction}>
          <DefaultDropdownSingleSelect options={options} closeOnClick={closeOnClick} />
        </DefaultDropdownMenu>
      </DropdownV2>
      <DefaultInput {...inputProps} hasLeftLabel />
    </InputWithLabel>
  );
};

/** @deprecated prefer `import { Menu } from '@workramp/ui';` */
const DefaultDropdownSingleSelect = ({ options, optionRenderer, className, closeOnClick = true }) => {
  const { handleClose } = useContext(DropdownContext);

  const handleClick = (option) => {
    !!option.onClick && option.onClick(option);
    closeOnClick && handleClose();
  };

  return (
    <MultiSelectList className={className}>
      {options.map((option, idx) => (
        <MultiSelectListItemWrapper key={idx} onClick={() => handleClick(option)} disabled={option.disabled}>
          {optionRenderer ? (
            optionRenderer(option)
          ) : (
            <MultiSelectListItem isActive={option.isActive}>
              <FullWidthTruncatedDiv text={option.displayText} numLines={1} />
            </MultiSelectListItem>
          )}
        </MultiSelectListItemWrapper>
      ))}
    </MultiSelectList>
  );
};

/** @deprecated prefer `import { Menu } from '@workramp/ui';` */
const DefaultDropdownMultiSelect = ({ options, className }) => (
  <MultiSelectList className={className}>
    {options.map(({ displayText, isChecked, onClick }, idx) => (
      <MultiSelectListItem key={idx} onClick={() => onClick(!isChecked)}>
        <StyledSettingCheckbox isSet={isChecked} />
        <FullWidthTruncatedDiv numLines={1} text={displayText} />
      </MultiSelectListItem>
    ))}
  </MultiSelectList>
);

/** @deprecated prefer `import { Menu } from '@workramp/ui';` */
const DefaultDropdownViewOnly = ({ options, className }) => (
  <MultiSelectList className={className}>
    {options.map(({ displayText }, idx) => (
      <MultiSelectListItem key={idx}>
        <FullWidthTruncatedDiv text={displayText} numLines={1} />
      </MultiSelectListItem>
    ))}
  </MultiSelectList>
);

/**
 * TODO: Add more documentation for this component as well as other components in this file.
 * NOTE: Supported directions are (W, E, NE, S, SW)
 * @param button
 * @param dropdownMenuProps
 * @param children
 * @returns {*}
 * @constructor
 * @deprecated prefer `import { Menu } from '@workramp/ui';`
 */
const DropdownV2 = ({ button, allowClick, children, onClose, onOpen, initialShow = false }) => {
  const [show, setShow] = useState(initialShow);
  const [disableButtonClick, setDisableButtonClick] = useState();
  const wrapperRef = useRef(null);

  const handleClose = () => {
    setShow(false);
    onClose && onClose();
  };

  const toggleMenu = () => {
    !show && onOpen && onOpen();
    setShow(!show);
    !!show && onClose && onClose();
  };

  useOutsideClick(wrapperRef, handleClose, allowClick, {
    shouldAddHandler: show,
    dependencies: [handleClose, onClose],
  });

  // TODO: better handle button caret and click action when one / no options available
  return (
    <DropdownContext.Provider value={{ show, setShow, disableButtonClick, setDisableButtonClick, handleClose }}>
      <Dropdown>
        <div ref={wrapperRef} role="listbox" aria-expanded={show}>
          <div
            onClick={toggleMenu}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              const { key } = event;
              if (key === 'Enter' || key === ' ') toggleMenu(event);
            }}
          >
            {button}
          </div>
          {show && allowClick && children}
        </div>
        {show && !allowClick && children}
      </Dropdown>
    </DropdownContext.Provider>
  );
};

export {
  DropdownV2,
  DefaultButton,
  DefaultDropdownMenu,
  DefaultDropdownHeader,
  DefaultInput,
  DefaultInputWithLabel,
  MultiSelectList,
  MultiSelectListItem,
  DefaultButtonWrapper,
  DefaultButtonContentWrapper,
  DefaultButtonDisplayText,
  DefaultButtonCaret,
  DefaultDropdownSingleSelect,
  DefaultDropdownMultiSelect,
  DropdownContext,
  DropdownMenuWrapper,
  DropdownMenuModal,
  DefaultDropdownViewOnly,
  DefaultDropdownMenuBody,
};

const spacers = {
  0: '0px',
  1: '4px',
  2: '8px',
  3: '16px',
  4: '24px',
};

const Dropdown = styled.div`
  position: relative;
`;

const DropdownMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 8px 16px;
`;

const DropdownMenuHeaderTitle = styled.div`
  opacity: 0.8;
  color: #1b324b;
  line-height: 24px;
`;

const DropdownMenuHeaderCloseWrapper = styled.div`
  height: 16px;
  width: 16px;
`;

const DropdownMenuHeaderCloseIcon = styled.img`
  cursor: pointer;
  margin: 2px;
`;

/** @deprecated prefer `import { Menu } from '@workramp/ui';` */
const DropdownMenuModal = styled.div`
  position: relative;
  z-index: 99;
  display: flex;
  width: 100%;
  max-height: ${({ fullHeight, maxHeight }) => {
    if (maxHeight) return maxHeight;
    return fullHeight ? '100%' : '66%';
  }};
  margin: auto 0;
  pointer-events: auto;
  flex-direction: column;
`;

/** @deprecated prefer `import { Button } from '@workramp/ui';` */
const DefaultButtonWrapper = styled.div`
  background-color: #ffffff;
  border: 1px solid #ededef;
  border-radius: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 32px;
  margin-right: 8px;
  cursor: ${({ disableClick }) => (disableClick ? 'default' : 'pointer')};
  disabled: ${({ disableClick }) => (disableClick ? 'true' : 'false')};

  &:last-child {
    margin-right: 0;
  }

  ${({ width }) => width && `width: ${width}px;`}
`;

const AutocompleteInputWrapper = styled.div`
  height: 40px;
  width: 100%;

  .autocomplete-input-wrapper {
    height: 40px;
    display: flex;
    box-sizing: border-box;
    width: 100%;
  }
  ${({ hasLeftLabel }) =>
    hasLeftLabel &&
    `
    .input-editor-2020 {
      border-radius: 0 4px 4px 0;
    }
  `}
`;

const DropdownInput = styled.input.attrs({
  className: 'input-editor-2020',
})`
  height: 40px;
  display: flex;
  ${({ hasLeftLabel }) =>
    hasLeftLabel &&
    `
    .input-editor-2020 {
      border-radius: 0 4px 4px 0;
    }
  `}
`;

const InputLabelButtonWrapper = styled.div`
  > div {
    height: 38px;
    border-radius: 4px 0 0 4px;
    border-right: 0;
    > div {
      > div {
        opacity: 0.8;
        color: #253256;
      }
    }
  }
`;

/** @deprecated prefer `import { Button } from '@workramp/ui';` */
const DefaultButtonContentWrapper = styled.div`
  display: flex;
  height: inherit;
  align-items: center;
  margin-left: 16px;
  margin-right: ${({ hideCaret }) => (hideCaret ? '16px' : '12px')};

  ${({ displayAsSelect }) => displayAsSelect && 'width: 100%; justify-content: space-between;'}
`;

/** @deprecated prefer `import { Button } from '@workramp/ui';` */
const DefaultButtonDisplayText = styled.div`
  font-size: 12px;
  color: #253256;
`;

/** @deprecated prefer `import { Button } from '@workramp/ui';` */
const DefaultButtonCaret = styled.img`
  width: 8px;
  height: 4px;
  opacity: 0.25;
  margin-left: 4px;
  ${({ show }) => show && `transform: rotate(180deg);`}
  transition: .2s;
`;

const westCaretBoxShadowStyles = css`
  box-shadow:
    0 0 0 1px #e6eaef,
    8px 0 8px -4px rgba(6, 8, 14, 0.16),
    0 32px 32px -16px rgba(6, 8, 14, 0.08);
`;

const eastCaretBoxShadowStyles = css`
  box-shadow:
    0 0 0 1px #e6eaef,
    -8px 0 8px -4px rgba(6, 8, 14, 0.16),
    0 32px 32px -16px rgba(6, 8, 14, 0.08);
`;

const northCaretBoxShadowStyles = css`
  box-shadow:
    0 0 0 1px #e6eaef,
    0 8px 8px -4px rgba(6, 8, 14, 0.16),
    0 32px 32px -16px rgba(6, 8, 14, 0.08);
`;

const southCaretBoxShadowStyles = css`
  box-shadow:
    0 0 0 1px #e6eaef,
    0 -8px 8px -4px rgba(6, 8, 14, 0.16),
    0 16px 32px -16px rgba(6, 8, 14, 0.08);
`;

const defaultDropShadowStyles = css`
  box-shadow:
    0 0 0 1px #e6eaef,
    0 8px 8px -4px rgba(6, 8, 14, 0.16),
    0 32px 32px -16px rgba(6, 8, 14, 0.08);
`;

const westCaretStyles = css`
  &::before {
    display: none;
  }

  &::after {
    top: 11px;
    right: -10px;
    transform: rotate(-90deg);
  }
`;

const eastCaretStyles = css`
  &::before {
    display: none;
  }

  &::after {
    top: 11px;
    left: -10px;
    transform: rotate(90deg);
  }
`;

const northCaretStyles = css`
  &::before {
    display: none;
  }

  &::after {
    bottom: -7px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const northWestCaretStyles = css`
  &::before {
    display: none;
  }

  &::after {
    bottom: -7px;
    left: 10px;
  }
`;

const northEastCaretStyles = css`
  &::before {
    display: none;
  }

  &::after {
    bottom: -7px;
    right: 10px;
  }
`;

const southCaretStyles = css`
  &::before {
    display: none;
  }

  &::after {
    top: -7px;
    right: 50%;
    transform: translateX(50%) rotate(180deg);
  }
`;

const southWestCaretStyles = css`
  &::before {
    display: none;
  }

  &::after {
    top: -7px;
    left: 10px;
    transform: rotate(180deg);
  }
`;

const southEastCaretStyles = css`
  &::before {
    display: none;
  }

  &::after {
    top: -7px;
    right: 10px;
    transform: rotate(180deg);
  }
`;

const defaultCaretStyles = css`
  top: 100%;
  right: 50%;

  margin-top: 8px;
  transform: translateX(50%);
`;

/** @deprecated prefer `import { Menu } from '@workramp/ui';` */
const DropdownMenuWrapper = styled.div`
  position: absolute;
  top: auto;
  bottom: auto;
  right: auto;
  left: auto;
  z-index: 110;
  width: ${({ width }) => {
    if (width === 'inherit') return 'inherit';

    return width ? `${width}px` : '280px';
  }};
  padding-top: ${spacers[1]};
  padding-bottom: ${spacers[1]};
  list-style: none;
  background: #fff;
  ${defaultDropShadowStyles};
  border-radius: 6px;
  ${({ height }) =>
    height &&
    `
    max-height: ${height}px;
    overflow: auto;
  `}

  ${({ withCaret }) =>
    withCaret &&
    `
    &::before,
    &::after {
      position: absolute;
      display: inline-block;
      content: '';
      top: auto;
      bottom: auto;
      right: auto;
      left: auto;
      border-top: 7px solid white;
      border-bottom: none;
      border-right: 7px solid transparent;
      border-left: 7px solid transparent;
    }

    > ul {
      list-style: none;
    }
  `}

  ${({ direction, withCaret, offsets }) => {
    switch (direction) {
      case 'W':
        return `
          top: 0;
          right: 100%;

          width: auto;
          margin-top: 0;
          margin-right: ${spacers[2]}};

          ${withCaret && westCaretStyles}
          ${withCaret && westCaretBoxShadowStyles}
        `;
      case 'E':
        return `
          top: 0;
          left: 100%;

          width: auto;
          margin-top: 0;
          margin-left: ${spacers[2]}};

          ${withCaret && eastCaretStyles}
          ${withCaret && eastCaretBoxShadowStyles}
        `;
      case 'N':
        return `
          bottom: 100%;
          right: 50%;

          margin-bottom: 8px;
          transform: translateX(50%);

          ${withCaret && northCaretStyles}
          ${withCaret && northCaretBoxShadowStyles}
        `;
      case 'NW':
        return `
          bottom: 100%;
          left: ${offsets?.left ? offsets.left : 0}px;

          margin-bottom: 8px;

          ${withCaret && northWestCaretStyles}
          ${withCaret && northCaretBoxShadowStyles}
        `;
      case 'NE':
        return `
          bottom: 100%;
          right: ${offsets?.right ? offsets.right : 0}px;;

          margin-bottom: 8px;

          ${withCaret && northEastCaretStyles}
          ${withCaret && northCaretBoxShadowStyles}
        `;
      case 'S':
        return `
          ${defaultCaretStyles}

          ${withCaret && southCaretStyles}
          ${withCaret && southCaretBoxShadowStyles}
        `;
      case 'SW':
        return `
          top: 100%;
          left: ${offsets?.left ? offsets.left : 0}px;

          margin-top: 8px;

          ${withCaret && southWestCaretStyles}
          ${withCaret && southCaretBoxShadowStyles}
        `;
      case 'SE':
        return `
          top: 100%;
          right: ${offsets?.right ? offsets.right : 0}px;

          margin-top: 8px;

          ${withCaret && southEastCaretStyles}
          ${withCaret && southCaretBoxShadowStyles}
        `;
      default:
        return `
          ${defaultCaretStyles}

          ${withCaret && southCaretStyles}
          ${withCaret && southCaretBoxShadowStyles}
        `;
    }
  }}
`;

/** @deprecated prefer `import { Menu } from '@workramp/ui';` */
const MultiSelectList = styled.div`
  position: relative;
  margin: 0;
  width: 100%;
  max-height: inherit;
  margin-bottom: -1px;
  flex: auto;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: #fff;
  -webkit-overflow-scrolling: touch;
`;

/** @deprecated prefer `import { Menu } from '@workramp/ui';` */
const MultiSelectListItem = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 16px;
  overflow: hidden;
  color: #24292e;
  text-align: left;
  cursor: pointer;
  background-color: #fff;
  border: 0;
  font-size: 14px;
  color: #253256;
  line-height: 24px;
  margin-bottom: 4px;

  ${({ isActive }) => isActive && `font-weight: 600;`}

  &:hover {
    background-color: #f6f8fa;
  }
`;

const StyledSettingCheckbox = styled(SettingCheckbox)`
  width: 100%;
  margin-bottom: 0;
  padding-top: 0;
  display: flex;
  margin-right: 8px;
  height: 12px;
  width: 12px;
`;

const InputWithLabel = styled.div`
  margin: 4px 16px;
  display: flex;
  height: 40px;
`;

const MultiSelectListItemWrapper = styled.div`
  width: 100%;
  ${(props) => props.disabled && `pointer-events: none;`}
`;

/** @deprecated prefer `import { Menu } from '@workramp/ui';` */
const DefaultDropdownMenuBody = styled.div`
  ${({ maxHeight }) => maxHeight != null && `max-height: ${maxHeight}px`};
`;
